import { useState } from 'react';
import { useFailingMessages } from '../hooks/useFailingMessages';
import type { EntityType, DocumentStatus, FailingMessage } from '../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertCircle, Download, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

export function FailingMessagesPanel() {
  const [page, setPage] = useState(0);
  const [entityFilter, setEntityFilter] = useState<EntityType | undefined>();
  const [statusFilter, setStatusFilter] = useState<DocumentStatus | undefined>();
  const limit = 50;
  const offset = page * limit;

  const { data, isLoading, error, refetch } = useFailingMessages({
    limit,
    offset,
    entityType: entityFilter,
    status: statusFilter,
  });

  // Export to CSV
  const exportToCSV = () => {
    if (!data?.documents || data.documents.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['Document ID', 'Entity ID', 'Entity Type', 'Status', 'Error Message', 'Retry Count', 'Last Error At', 'First Failed At', 'Permanent Failure'];
    const rows = data.documents.map((doc: FailingMessage): string[] => [
      doc.documentId,
      doc.entityId,
      doc.entityType,
      doc.status,
      `"${doc.fullErrorMessage.replace(/"/g, '""')}"`, // Escape quotes in CSV
      doc.retryCount.toString(),
      doc.lastErrorAt || '',
      doc.firstFailedAt || '',
      doc.isPermanentFailure ? 'Yes' : 'No',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row: string[]) => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `failing-messages-${new Date().toISOString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export to JSON
  const exportToJSON = () => {
    if (!data?.documents || data.documents.length === 0) {
      alert('No data to export');
      return;
    }

    const jsonContent = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `failing-messages-${new Date().toISOString()}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadgeColor = (status: DocumentStatus) => {
    switch (status) {
      case 'index_error':
        return 'bg-red-500';
      case 'ai_error':
        return 'bg-orange-500';
      case 'retry_pending':
        return 'bg-yellow-500';
      case 'failed':
        return 'bg-gray-500';
      default:
        return 'bg-blue-500';
    }
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const totalPages = data?.summary?.total ? Math.ceil(data.summary.total / limit) : 0;

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-2xl flex items-center gap-2">
              <AlertCircle className="h-6 w-6 text-orange-500" />
              Failing Messages & Retry Queue
            </CardTitle>
            <CardDescription className="mt-2">
              Documents with errors and retry details. Auto-retry up to 3 times every 10 minutes.
              After max retries, marked as permanent failure.
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportToCSV}
              disabled={!data?.documents || data.documents.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={exportToJSON}
              disabled={!data?.documents || data.documents.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1">
            <Select
              value={entityFilter || 'all'}
              onValueChange={(value) => {
                setEntityFilter(value === 'all' ? undefined : (value as EntityType));
                setPage(0); // Reset to first page
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Entity Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entity Types</SelectItem>
                <SelectItem value="person">Person</SelectItem>
                <SelectItem value="company">Company</SelectItem>
                <SelectItem value="opportunity">Opportunity</SelectItem>
                <SelectItem value="note">Note</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Select
              value={statusFilter || 'all'}
              onValueChange={(value) => {
                setStatusFilter(value === 'all' ? undefined : (value as DocumentStatus));
                setPage(0); // Reset to first page
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="index_error">Index Error</SelectItem>
                <SelectItem value="ai_error">AI Error</SelectItem>
                <SelectItem value="retry_pending">Retry Pending</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Summary Stats */}
        {data?.summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total Failing</CardDescription>
                <CardTitle className="text-3xl">{data.summary.total}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Index Errors</CardDescription>
                <CardTitle className="text-3xl text-red-500">{data.summary.byStatus.index_error}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>AI Errors</CardDescription>
                <CardTitle className="text-3xl text-orange-500">{data.summary.byStatus.ai_error}</CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Retry Pending</CardDescription>
                <CardTitle className="text-3xl text-yellow-500">{data.summary.byStatus.retry_pending}</CardTitle>
              </CardHeader>
            </Card>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-8 text-red-500">
            <AlertCircle className="h-12 w-12 mx-auto mb-4" />
            <p>Error loading failing messages</p>
            <p className="text-sm text-gray-500">{(error as Error).message}</p>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-8 text-gray-500">
            <RefreshCw className="h-12 w-12 mx-auto mb-4 animate-spin" />
            <p>Loading failing messages...</p>
          </div>
        )}

        {/* Table */}
        {!isLoading && !error && data?.documents && (
          <>
            {data.documents.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <AlertCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
                <p className="text-lg font-medium text-green-600">No failing messages! ✅</p>
                <p className="text-sm mt-2">All documents are processing successfully.</p>
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Entity ID</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Error Message</TableHead>
                        <TableHead>Retries</TableHead>
                        <TableHead>Last Error</TableHead>
                        <TableHead>First Failed</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.documents.map((doc: FailingMessage) => (
                        <TableRow key={doc.documentId}>
                          <TableCell className="font-mono text-sm">{doc.entityId}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{doc.entityType}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusBadgeColor(doc.status)}>
                              {doc.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-md">
                            <div className="truncate" title={doc.fullErrorMessage}>
                              {doc.errorMessage}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={doc.isPermanentFailure ? 'destructive' : 'secondary'}>
                              {doc.retryCount}{doc.isPermanentFailure ? ' (max)' : ''}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {formatTimestamp(doc.lastErrorAt)}
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {formatTimestamp(doc.firstFailedAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-gray-500">
                    Showing {offset + 1} to {Math.min(offset + limit, data.summary.total)} of {data.summary.total}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <div className="flex items-center px-4 text-sm">
                      Page {page + 1} of {totalPages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages - 1}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
